import React from 'react';
import LegalLayout, { COMPANY, H2, P, UL } from './LegalLayout';

const Terms: React.FC = () => (
  <LegalLayout
    slug="terms"
    title="Terms of Service"
    description="The terms governing your use of 12and22 hosting, VPS, domain, and business email services."
  >
    <P>
      These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the websites,
      products, and services (collectively, the &ldquo;Services&rdquo;) provided by {COMPANY.legalName},
      operating as {COMPANY.brand} (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;). By creating an account or
      using the Services, you agree to these Terms. If you do not agree, do not use the Services.
    </P>

    <H2>1. Accounts</H2>
    <P>
      You must provide accurate information when registering and keep it up to date. You are
      responsible for safeguarding your account credentials and for all activity under your account.
      You must verify your email address before accessing certain features. You must be at least 18
      years old, or the age of majority in your jurisdiction, to purchase Services.
    </P>

    <H2>2. Services and provisioning</H2>
    <P>
      We provide shared web hosting, virtual private servers (VPS), domain registration and transfer,
      and business email. Provisioning is automated where possible; some steps (for example, pointing
      an existing domain&apos;s DNS to us) require action on your part. Service availability is subject to
      our <a href="/sla" className="text-primary-navy underline">Service Level Agreement</a>.
    </P>

    <H2>3. Fees, billing, and renewals</H2>
    <UL>
      <li>Fees are stated at checkout and charged in U.S. Dollars (USD).</li>
      <li>Subscriptions renew automatically at the then-current rate until cancelled. You authorise us
        to charge your payment method on each renewal.</li>
      <li>You may cancel at any time; cancellation stops future renewals and takes effect at the end of
        the current billing period.</li>
      <li>Refunds are governed by our <a href="/refund" className="text-primary-navy underline">Refund Policy</a>.</li>
      <li>Domain registrations and transfers are billed by third-party registries and are
        non-refundable once submitted.</li>
    </UL>

    <H2>4. Acceptable use</H2>
    <P>
      Your use of the Services is subject to our{' '}
      <a href="/aup" className="text-primary-navy underline">Acceptable Use Policy</a>. We may suspend or
      terminate Services that violate it, that create security or stability risks, or that we are
      legally required to act on.
    </P>

    <H2>5. Your content and responsibilities</H2>
    <P>
      You retain ownership of the content and data you host with us. You are responsible for the
      lawfulness of your content, for maintaining your own backups, and for the security of your
      applications, software, and passwords. We provide backup features as a convenience, not as a
      substitute for your own backups.
    </P>

    <H2>6. Suspension and termination</H2>
    <P>
      We may suspend or terminate your Services for non-payment, violation of these Terms or the
      Acceptable Use Policy, or to protect the Services, other customers, or third parties. Where
      practical and lawful, we will give notice. On termination, your right to use the Services ends
      and hosted data may be deleted after a reasonable retention period.
    </P>

    <H2>7. Warranties and disclaimers</H2>
    <P>
      The Services are provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; To the maximum extent permitted by
      law, we disclaim all warranties, express or implied, including merchantability, fitness for a
      particular purpose, and non-infringement. We do not warrant that the Services will be
      uninterrupted or error-free beyond the commitments in our SLA.
    </P>

    <H2>8. Limitation of liability</H2>
    <P>
      To the maximum extent permitted by law, {COMPANY.legalName} will not be liable for any indirect,
      incidental, special, consequential, or punitive damages, or for lost profits, revenue, or data.
      Our total liability for any claim arising out of or relating to the Services is limited to the
      amount you paid us for the affected Service in the three (3) months preceding the claim.
    </P>

    <H2>9. Indemnification</H2>
    <P>
      You agree to indemnify and hold harmless {COMPANY.legalName} and its officers, employees, and
      agents from claims arising out of your content, your use of the Services, or your violation of
      these Terms or applicable law.
    </P>

    <H2>10. Changes to the Services or Terms</H2>
    <P>
      We may modify the Services or these Terms. Material changes will be posted here with an updated
      effective date and, where appropriate, communicated by email. Your continued use after changes
      take effect constitutes acceptance.
    </P>

    <H2>11. Governing law</H2>
    <P>
      These Terms are governed by the laws of the {COMPANY.jurisdiction}, without regard to conflict-of-law
      principles. The exclusive venue for disputes is the state and federal courts located in New York,
      to the extent permitted by law.
    </P>

    <H2>12. Contact</H2>
    <P>
      {COMPANY.legalName}, {COMPANY.address}. Email:{' '}
      <a href={`mailto:${COMPANY.legalEmail}`} className="text-primary-navy underline">{COMPANY.legalEmail}</a>.
    </P>
  </LegalLayout>
);

export default Terms;
